"use client"

import type React from "react"
import { useState, useEffect, useRef, useCallback } from "react"
import { useParams } from "react-router-dom"
import axios from "axios"
import {
  FiPlay,
  FiPause,
  FiChevronLeft,
  FiChevronRight,
  FiZoomIn,
  FiZoomOut,
  FiMaximize2,
  FiMinimize2,
  FiInfo,
  FiClock,
  FiAlertTriangle,
  FiImage,
  FiVideo,
  FiExternalLink,
  FiFile,
  FiDownload,
} from "react-icons/fi"

interface SiteInfo {
  id: number
  site_id: string
  site_name: string
  site_address: string
  project: {
    id: number
    project_name: string
    activity?: { id: number; activity_name: string }
  }
}

interface SlideshowInfo {
  expiry_at: string
  generated_by: { id: number; first_name: string; last_name: string; username: string } | null
  last_regenerated_at: string | null
  created_at: string
}

interface MediaItem {
  id: number
  data_type?: "photo" | "video" | "document"
  file_name: string
  file_path: string
  file_size?: number | string
  file_type?: string
  mime_type?: string
  upload_stage?: string
  uploaded_at?: string
  created_at?: string
  section?: string
}

function getMediaType(item: MediaItem): "photo" | "video" | "document" {
  if (item.data_type && item.data_type !== "photo") return item.data_type
  const mime = item.mime_type?.toLowerCase() || ""
  const ext = item.file_path.split("?")[0].split(".").pop()?.toLowerCase() || ""
  if (["mp4", "webm", "mov", "avi", "mkv"].includes(ext) || mime.startsWith("video/")) return "video"
  const docExts = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "zip", "rar", "txt", "csv"]
  const docMimes = ["application/pdf", "application/msword", "application/vnd", "text/", "application/zip"]
  if (docExts.includes(ext) || docMimes.some((m) => mime.startsWith(m))) return "document"
  return "photo"
}

const SPEED_OPTIONS = [
  { label: "0.5×", value: 8000 },
  { label: "1×", value: 4000 },
  { label: "2×", value: 2000 },
  { label: "4×", value: 1000 },
]

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function getMediaUrl(path: string): string {
  const base = process.env.REACT_APP_API_URL?.replace(/\/v1$/, "") || ""
  if (path.startsWith("http")) return path
  return `${base}/${path.replace(/^\//, "")}`
}

function formatGeneratorName(
  user?: { first_name?: string; last_name?: string; username?: string } | null
) {
  if (!user) return "Unknown user"
  const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
  return fullName || user.username || "Unknown user"
}

export const SlideshowPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [site, setSite] = useState<SiteInfo | null>(null)
  const [slideshowInfo, setSlideshowInfo] = useState<SlideshowInfo | null>(null)
  const [media, setMedia] = useState<MediaItem[]>([])

  const [current, setCurrent] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [speedIndex, setSpeedIndex] = useState(1) // default 1× = 4000ms
  const [zoom, setZoom] = useState(1)
  const [fullscreen, setFullscreen] = useState(false)
  const [showInfo, setShowInfo] = useState(true)

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!slug) return
    const apiBase = process.env.REACT_APP_API_URL || ""
    axios
      .get(`${apiBase}/v1/slideshow/public/${slug}`)
      .then((res) => {
        if (res.data.success) {
          setSite(res.data.data.site)
          setSlideshowInfo(res.data.data.slideshow)
          const rawMedia: MediaItem[] = res.data.data.media || []
          setMedia(rawMedia.map((item) => ({ ...item, data_type: getMediaType(item) })))
        } else {
          setError(res.data.message || "Failed to load slideshow")
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Slideshow not found or link has expired")
      })
      .finally(() => setLoading(false))
  }, [slug])

  const advance = useCallback(() => {
    setCurrent((prev) => (prev + 1) % media.length)
  }, [media.length])

  const retreat = useCallback(() => {
    setCurrent((prev) => (prev - 1 + media.length) % media.length)
  }, [media.length])

  // Auto-advance timer
  useEffect(() => {
    if (!playing || media.length === 0) return
    const currentItem = media[current]
    // For videos, let them play through naturally (don't auto-advance)
    if (currentItem && getMediaType(currentItem) === "video") return

    const delay = SPEED_OPTIONS[speedIndex].value
    timerRef.current = setTimeout(advance, delay)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [playing, current, speedIndex, media, advance])

  // When video ends, advance
  const handleVideoEnded = () => { if (playing) advance() }

  // Reset zoom when slide changes
  useEffect(() => { setZoom(1) }, [current])

  const toggleFullscreen = () => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen().then(() => setFullscreen(true))
    } else if (document.fullscreenElement) {
      document.exitFullscreen().then(() => setFullscreen(false))
    }
  }

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement)
    document.addEventListener("fullscreenchange", handler)
    return () => document.removeEventListener("fullscreenchange", handler)
  }, [])

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") advance()
      if (e.key === "ArrowLeft") retreat()
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p) }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [advance, retreat])

  const isExpiringSoon = slideshowInfo
    ? new Date(slideshowInfo.expiry_at).getTime() - Date.now() < 24 * 60 * 60 * 1000
    : false

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p>Loading slideshow...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="bg-gray-800 rounded-xl p-8 max-w-md text-center shadow-2xl">
          <FiAlertTriangle className="w-14 h-14 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Slideshow Unavailable</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    )
  }

  if (media.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="bg-gray-800 rounded-xl p-8 max-w-md text-center shadow-2xl">
          <FiImage className="w-14 h-14 text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No Media</h2>
          <p className="text-gray-400">This site doesn't have any photos or videos yet.</p>
          {site && (
            <p className="text-xs text-gray-500 mt-3">
              Site: {site.site_id} — {site.project?.project_name}
            </p>
          )}
        </div>
      </div>
    )
  }

  const currentItem = media[current]
  const mediaUrl = getMediaUrl(currentItem.file_path)

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-black flex flex-col select-none"
      style={{ fontFamily: "system-ui, sans-serif" }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-b from-black/80 to-transparent absolute top-0 left-0 right-0 z-20">
        {/* Site info */}
        <div className="text-white min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold truncate">{site?.site_id}</span>
            {site?.project?.activity && (
              <span className="text-xs bg-white/10 rounded px-2 py-0.5 truncate">
                {site.project.activity.activity_name}
              </span>
            )}
          </div>
          <p className="text-xs text-white/60 truncate">{site?.project?.project_name}</p>
        </div>

        {/* Counter + controls */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <span className="text-xs text-white/70">
            {current + 1} / {media.length}
          </span>
          {getMediaType(currentItem) === "photo" ? (
            <FiImage className="w-3.5 h-3.5 text-white/50" />
          ) : (
            <FiVideo className="w-3.5 h-3.5 text-white/50" />
          )}
          <button
            onClick={() => setShowInfo((v) => !v)}
            className="p-1.5 rounded text-white/70 hover:text-white hover:bg-white/10"
            title="Toggle info panel"
          >
            <FiInfo className="w-4 h-4" />
          </button>
          <a
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded text-white/70 hover:text-white hover:bg-white/10"
            title="Open in new tab"
          >
            <FiExternalLink className="w-4 h-4" />
          </a>
          <button
            onClick={toggleFullscreen}
            className="p-1.5 rounded text-white/70 hover:text-white hover:bg-white/10"
            title="Toggle fullscreen"
          >
            {fullscreen ? <FiMinimize2 className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Main media area */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center bg-black">
        {/* Navigation arrows */}
        <button
          onClick={retreat}
          className="absolute left-2 z-10 p-2 rounded-full bg-black/40 text-white hover:bg-black/70 transition"
        >
          <FiChevronLeft className="w-7 h-7" />
        </button>
        <button
          onClick={advance}
          className="absolute right-2 z-10 p-2 rounded-full bg-black/40 text-white hover:bg-black/70 transition"
        >
          <FiChevronRight className="w-7 h-7" />
        </button>

        {/* Media */}
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            transform: `scale(${zoom})`,
            transition: "transform 0.2s ease",
          }}
        >
          {getMediaType(currentItem) === "photo" ? (
            <img
              key={currentItem.id}
              src={mediaUrl}
              alt={currentItem.file_name}
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: "calc(100vh - 120px)" }}
            />
          ) : getMediaType(currentItem) === "video" ? (
            <video
              key={currentItem.id}
              ref={videoRef}
              src={mediaUrl}
              className="max-w-full max-h-full"
              style={{ maxHeight: "calc(100vh - 120px)" }}
              controls
              autoPlay={playing}
              onEnded={handleVideoEnded}
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 p-8 bg-gray-900/60 rounded-xl max-w-md">
              <FiFile className="w-16 h-16 text-blue-300" />
              <div className="text-center">
                <p className="text-white font-medium text-sm truncate max-w-xs">{currentItem.file_name}</p>
                {currentItem.upload_stage && (
                  <p className="text-white/50 text-xs mt-1 capitalize">{currentItem.upload_stage.replace(/_/g, " ")}</p>
                )}
              </div>
              <a
                href={mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition"
              >
                <FiDownload className="w-4 h-4" />
                Download Document
              </a>
            </div>
          )}
        </div>

        {/* Section badge */}
        {currentItem.section && (
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
            {currentItem.section}
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3 z-20">
        <div className="flex items-center justify-between gap-3">
          {/* Play/Pause + Speed */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPlaying((p) => !p)}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition"
              title={playing ? "Pause" : "Play"}
            >
              {playing ? <FiPause className="w-5 h-5" /> : <FiPlay className="w-5 h-5" />}
            </button>

            <div className="flex items-center gap-1">
              {SPEED_OPTIONS.map((opt, i) => (
                <button
                  key={opt.label}
                  onClick={() => setSpeedIndex(i)}
                  className={`text-xs px-2 py-1 rounded transition ${
                    i === speedIndex
                      ? "bg-white text-black font-medium"
                      : "bg-white/15 text-white hover:bg-white/25"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Zoom */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
              disabled={zoom <= 0.5}
              className="p-1.5 rounded bg-white/15 text-white hover:bg-white/25 disabled:opacity-40"
              title="Zoom out"
            >
              <FiZoomOut className="w-4 h-4" />
            </button>
            <span className="text-white text-xs w-14 text-center">{Math.round(zoom * 100)}%</span>
            <button
              onClick={() => setZoom((z) => Math.min(10, z + 0.25))}
              disabled={zoom >= 10}
              className="p-1.5 rounded bg-white/15 text-white hover:bg-white/25 disabled:opacity-40"
              title="Zoom in"
            >
              <FiZoomIn className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Thumbnail strip */}
        <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-white/20">
          {media.map((item, i) => (
            <button
              key={item.id}
              onClick={() => { setCurrent(i); setPlaying(false) }}
              className={`flex-shrink-0 w-12 h-9 rounded overflow-hidden border-2 transition ${
                i === current ? "border-white" : "border-transparent opacity-50 hover:opacity-80"
              }`}
            >
              {getMediaType(item) === "photo" ? (
                <img
                  src={getMediaUrl(item.file_path)}
                  alt={item.file_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                  <FiVideo className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Info panel */}
      {showInfo && slideshowInfo && (
        <div className="absolute top-14 right-3 z-30 bg-black/75 backdrop-blur-sm text-white rounded-lg p-3 max-w-xs text-xs shadow-xl">
          <p className="font-semibold text-sm mb-2 flex items-center gap-1">
            <FiInfo className="w-3.5 h-3.5" /> Site Information
          </p>
          <div className="space-y-1 text-white/70">
            <div><span className="text-white/50">Site ID:</span> {site?.site_id}</div>
            <div><span className="text-white/50">Project:</span> {site?.project?.project_name}</div>
            {site?.project?.activity && (
              <div><span className="text-white/50">Activity:</span> {site.project.activity.activity_name}</div>
            )}
          </div>
          <hr className="border-white/10 my-2" />
          <div className="space-y-1 text-white/70">
            <div className={`flex items-center gap-1 ${isExpiringSoon ? "text-yellow-300" : ""}`}>
              {isExpiringSoon && <FiClock className="w-3 h-3 text-yellow-300" />}
              <span className="text-white/50">Expires:</span>{" "}
              {formatDate(slideshowInfo.expiry_at)}
              {isExpiringSoon && <span className="text-yellow-300 ml-1">(soon)</span>}
            </div>
            {(slideshowInfo.last_regenerated_at || slideshowInfo.created_at) && (
              <div>
                <span className="text-white/50">Regenerated:</span>{" "}
                {formatDate(slideshowInfo.last_regenerated_at || slideshowInfo.created_at)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SlideshowPage
